import { useEffect, useMemo, useState } from "react";
import { getContractApply } from "src/api/contract/contract-api";
import { ContractManageList } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { getContractDeviceAsync, getProductDeviceAsync } from "src/api/iot/iot-api";
import { ContractDeviceModel, DeviceModel, ProductDeviceModel } from "src/api/iot/iot-types";
import { getProductListAsync } from "src/api/product/product-api";
import { ProductModel } from "src/api/product/product-types";
import { PageMeta } from "src/api/public-types";
import { BasePagination } from "src/components";
import InnerTable from "src/components/InnerTable";
import { iotConnectedContractColumns, iotConnectedProductColumns } from "../../columns/IotConnectedColumns";

type Props = {
  type: "product" | "contract";
  iotData: DeviceModel;
  onClose: () => void;
};

const IotConnectedListModal = ({ iotData, type, onClose }: Props) => {
  const [devices, setDevices] = useState<any[]>([]);
  const [pageMeta, setPageMeta] = useState<PageMeta>();
  const columns = useMemo(() => (type === "product" ? iotConnectedProductColumns : iotConnectedContractColumns), [type]);
  const [searchParams, setSearchParams] = useState<{ page: number; size: number }>({ page: 0, size: 20 });

  const { executeAsync: getProductDevice } = useApiOperation(getProductDeviceAsync);
  const { executeAsync: getProductList } = useApiOperation(getProductListAsync);
  const { executeAsync: getContractDevice } = useApiOperation(getContractDeviceAsync);
  const { executeAsync: getContractList } = useApiOperation(getContractApply);

  const fetchDevices = async (iotData: DeviceModel) => {
    // let result: ProductDeviceModel[] | ContractDeviceModel[] = [];

    // 상품 디바이스 목록 조회
    if (type === "product") {
      // 1. 상품 디바이스 목록 조회
      const { data } = await getProductDevice({
        ...searchParams,
        registeredDeviceId: iotData.registeredDeviceId,
        buildingId: iotData.building.id,
        buildingLocation: [{ category: iotData.building.location.category, identifier: iotData.building.location.identifier }],
      });

      const productIds = data.data.content.map((item: ProductDeviceModel) => String(item.productId));

      if (productIds.length > 0) {
        // 2. 상품 목록 조회
        const { data: productList } = await getProductList({
          page: 0,
          size: productIds.length,
          id: productIds.join(","),
        });
        setDevices(
          productList.data.content.map((item: ProductModel) => ({ id: item.id, productName: item.productName, productType: item.productType })),
        );
        setPageMeta(data.meta.pageMeta);
      }
    }
    // 계약 디바이스 목록 조회
    else {
      const { data } = await getContractDevice({
        ...searchParams,
        registeredDeviceId: iotData.registeredDeviceId,
        buildingId: iotData.building.id,
        buildingLocation: [{ category: iotData.building.location.category, identifier: iotData.building.location.identifier }],
      });
      const contractManageIds = data.data.content.map((item: ContractDeviceModel) => String(item.contractManageId));
      if (contractManageIds.length > 0) {
        // 2. 계약 목록 조회
        const { data: contractList } = await getContractList({
          page: 0,
          size: contractManageIds.length,
          contractManageIds: contractManageIds.join(","),
        });
        setDevices(
          contractList.data.content.map((item: ContractManageList) => ({
            contractId: item.contractId,
            contractManageId: item.contractManageId,
            contractName: item.contractName,
            productName: item.spaceProductName,
            contractStartTime: item.contractStartTime,
            contractEndTime: item.contractEndTime,
            contractStep: item.contractStep,
          })),
        );
        setPageMeta(data.meta.pageMeta);
      }
    }
  };

  useEffect(() => {
    fetchDevices(iotData);
  }, [iotData, searchParams]);

  return (
    <>
      <section className="base-abstract-modal__title flex-center-between ">
        <h1>{type === "product" ? "상품 " : "신청/계약"}</h1>
      </section>
      <section className="ase-abstract-modal__contents">
        <div className="contents-container__table px30">
          <InnerTable data={devices} columns={columns} />
        </div>
      </section>

      <section className="px30">
        <BasePagination
          pageIndex={pageMeta?.pageRequest.page || 0}
          totalPages={pageMeta?.totalPages || 0}
          currentSize={searchParams.size || 20}
          sizeOption={(size: number) => setSearchParams({ ...searchParams, page: 0, size })}
          goPage={(page: number) => setSearchParams({ ...searchParams, page })}
          totalElements={pageMeta?.totalElements || 0}
          children={
            <div className="flex-center">
              <button className="base-btn" onClick={onClose}>
                확인
              </button>
            </div>
          }
        />
      </section>
    </>
  );
};

export default IotConnectedListModal;
